import React, { useEffect, useState, useRef } from 'react';
import { ResultData, Description, ChiSquareResult } from '../types';
import { adminSettings } from '../config';
import { jStat } from 'jstat';
import { loadSurveyResults, clearSurveyResults } from '../utils/loadSurvey';
import { isDevelopment } from '../utils/env';
import { getZodiacSign } from '../utils/zodiac';
import { getBirthdayDescription } from '../utils/birthday';
import { getDescription } from '../utils/descriptions';

// Import recharts components for data visualization
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface SurveyResult {
  id: string;
  participantName: string;
  birthday: string;
  selectedDescription: string;
  selectedIsReal: boolean;
  actualSign: string;
  isMatch: boolean;
  timestamp: number;
  beliefLevel: string;
  knowledgeLevel: string;
  horoscopeReading: string;
  astrologicalInfluence: string;
}

interface AnalysisResults {
  totalResponses: number;
  correctMatches: number;
  matchRate: number;
  expectedRate: number;
  chiSquare: number;
  pValue: number;
  isSignificant: boolean;
}

interface AnalysisPageProps {
  resultsData: SurveyResult[];
  clearAllResults: () => void;
  toggleAnalysisMode: () => void;
  chiSquareResults: ChiSquareResult | null;
  setChiSquareResults: (results: ChiSquareResult) => void;
  realDescriptions: Description[];
  fakeDescriptions: Description[];
  onDeleteResult?: (resultId: string) => Promise<void>;
}

// Add new interfaces for filters
interface FilterState {
  beliefLevel: string[];
  knowledgeLevel: string[];
  horoscopeReading: string[];
  astrologicalInfluence: string[];
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  options: string[];
  selectedOptions: string[];
  onToggleOption: (option: string) => void;
}

// Add new interface for sort state
interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

// Add new interface for filter counts
interface FilterCounts {
  beliefLevel: number;
  knowledgeLevel: number;
  horoscopeReading: number;
  astrologicalInfluence: number;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  title,
  options,
  selectedOptions,
  onToggleOption
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content filter-modal">
        <h3>{title}</h3>
        <p className="filter-instructions">Uncheck options to exclude them from the analysis</p>
        <div className="filter-options">
          {options.map(option => (
            <label key={option} className="filter-option">
              <input
                type="checkbox"
                checked={selectedOptions.includes(option)}
                onChange={() => onToggleOption(option)}
              />
              {option}
            </label>
          ))}
        </div>
        <div className="modal-buttons">
          <button onClick={onClose} className="btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Add default filter options
const DEFAULT_FILTER_OPTIONS = {
  beliefLevel: [
    "Yes, totally!",
    "I'm open to it but skeptical.",
    "Not really, but it's fun.",
    "Nope, it's complete nonsense.",
    "Not provided"
  ],
  knowledgeLevel: [
    "Expert",
    "Intermediate",
    "Beginner",
    "None",
    "Not provided"
  ],
  horoscopeReading: [
    "Daily",
    "Weekly",
    "Monthly",
    "Never",
    "Not provided"
  ],
  astrologicalInfluence: [
    "Strong",
    "Moderate",
    "Weak",
    "None",
    "Not provided"
  ]
};

// Helper function to calculate chi-square p-value
const calculateChiSquarePValue = (chiSquare: number, degreesOfFreedom: number = 1): number => {
  // Use jstat to calculate the p-value from the chi-square distribution
  const pValue = 1 - jStat.chisquare.cdf(chiSquare, degreesOfFreedom);
  console.log('Chi-square p-value calculation:', {
    chiSquare,
    degreesOfFreedom,
    pValue,
    cdf: jStat.chisquare.cdf(chiSquare, degreesOfFreedom),
    '1-cdf': 1 - jStat.chisquare.cdf(chiSquare, degreesOfFreedom)
  });
  return pValue;
};

// Add new calculateStatistics function before the AnalysisPage component
const calculateStatistics = (data: SurveyResult[]) => {
  const totalParticipants = data.length;
  const correctMatches = data.filter((r: SurveyResult) => r.actualSign === r.selectedDescription).length;
  const incorrectMatches = totalParticipants - correctMatches;
  const expectedMatches = totalParticipants / 6; // 1/6 chance of matching your sign
  const expectedIncorrectMatches = totalParticipants - expectedMatches;
  
  // Calculate chi-square statistic with clearer formula
  const chiSquare = ((correctMatches - expectedMatches) ** 2 / expectedMatches) +
                   ((incorrectMatches - expectedIncorrectMatches) ** 2 / expectedIncorrectMatches);
  
  // Calculate p-value using jstat's chi-square distribution
  const pValue = calculateChiSquarePValue(chiSquare);
  const isSignificant = pValue < 0.05;

  console.log('Statistics calculation details:', {
    totalParticipants,
    correctMatches,
    incorrectMatches,
    expectedMatches,
    expectedIncorrectMatches,
    chiSquare,
    pValue,
    isSignificant,
    'chi-square components': {
      correct: ((correctMatches - expectedMatches) ** 2 / expectedMatches),
      incorrect: ((incorrectMatches - expectedIncorrectMatches) ** 2 / expectedIncorrectMatches)
    }
  });

  return {
    totalResponses: totalParticipants,
    correctMatches,
    matchRate: (correctMatches / totalParticipants) * 100,
    expectedRate: (expectedMatches / totalParticipants) * 100,
    chiSquare,
    pValue,
    isSignificant
  };
};

const AnalysisPage: React.FC<AnalysisPageProps> = ({ 
  resultsData, 
  clearAllResults, 
  toggleAnalysisMode, 
  chiSquareResults,
  setChiSquareResults,
  realDescriptions,
  fakeDescriptions,
  onDeleteResult
}: AnalysisPageProps) => {
  const [activeTab, setActiveTab] = useState<"summary" | "charts" | "rawData">("summary");
  const [results, setResults] = useState<AnalysisResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Add state for active filter modal
  const [activeFilter, setActiveFilter] = useState<keyof FilterState | null>(null);

  // Initialize filters with default options
  const [filters, setFilters] = useState<FilterState>(() => {
    return {
      beliefLevel: DEFAULT_FILTER_OPTIONS.beliefLevel,
      knowledgeLevel: DEFAULT_FILTER_OPTIONS.knowledgeLevel,
      horoscopeReading: DEFAULT_FILTER_OPTIONS.horoscopeReading,
      astrologicalInfluence: DEFAULT_FILTER_OPTIONS.astrologicalInfluence
    };
  });

  // Add state for filter counts
  const [filterCounts, setFilterCounts] = useState<FilterCounts>({
    beliefLevel: 0,
    knowledgeLevel: 0,
    horoscopeReading: 0,
    astrologicalInfluence: 0
  });

  // Add a ref to track if we've already loaded data
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const fetchResults = async () => {
      // Skip if we've already loaded data
      if (hasLoadedRef.current) {
        console.log('Skipping data load - already loaded');
        return;
      }

      try {
        console.log('=== FETCHING RESULTS IN ANALYSIS PAGE ===');
        const loadedResults = await loadSurveyResults();
        console.log(`Loaded ${loadedResults.length} results`);
        
        // Mark as loaded
        hasLoadedRef.current = true;
      } catch (err) {
        console.error('Error in fetchResults:', err);
        setError(err instanceof Error ? err.message : "Failed to fetch results");
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, []); // Empty dependency array since we only want to load once

  // Update filtered data computation to use memoization
  const filteredData = React.useMemo(() => {
    console.log('Computing filtered data');
    const filtered = resultsData.filter(result => {
      // Only apply filters that have values
      const beliefMatch = filters.beliefLevel.length === 0 || 
        filters.beliefLevel.includes(result.beliefLevel || 'Not provided');
      const knowledgeMatch = filters.knowledgeLevel.length === 0 || 
        filters.knowledgeLevel.includes(result.knowledgeLevel || 'Not provided');
      const horoscopeMatch = filters.horoscopeReading.length === 0 || 
        filters.horoscopeReading.includes(result.horoscopeReading || 'Not provided');
      const influenceMatch = filters.astrologicalInfluence.length === 0 || 
        filters.astrologicalInfluence.includes(result.astrologicalInfluence || 'Not provided');

      return beliefMatch && knowledgeMatch && horoscopeMatch && influenceMatch;
    });
    console.log('Filtered data length:', filtered.length);
    return filtered;
  }, [resultsData, filters]);

  // Add new useEffect to calculate statistics when filteredData changes
  useEffect(() => {
    // Only calculate if we have data and filters are initialized
    if (filteredData.length > 0 && 
        Object.keys(filters).length > 0 && 
        Object.keys(filterCounts).length > 0 &&
        !loading) {  // Add loading check
      console.log('Calculating statistics with filtered data:', {
        dataLength: filteredData.length,
        filters: Object.keys(filters).length,
        filterCounts: Object.keys(filterCounts).length,
        loading
      });
      
      const stats = calculateStatistics(filteredData);
      setResults(stats);
      
      // Update chiSquareResults state
      setChiSquareResults({
        chiSquare: stats.chiSquare,
        pValue: stats.pValue,
        isSignificant: stats.isSignificant,
        expectedMatches: filteredData.length * (1/6), // 1/6 chance of matching your sign
        observedMatches: stats.correctMatches,
        degreesOfFreedom: 1
      });
    }
  }, [filteredData, filters, filterCounts, setChiSquareResults, loading]);

  // Add a separate useEffect for initial calculation
  useEffect(() => {
    if (resultsData && resultsData.length > 0 && !loading) {
      console.log('Initial statistics calculation with results data:', {
        dataLength: resultsData.length,
        loading
      });
      
      const stats = calculateStatistics(resultsData);
      setResults(stats);
      
      // Update chiSquareResults state
      setChiSquareResults({
        chiSquare: stats.chiSquare,
        pValue: stats.pValue,
        isSignificant: stats.isSignificant,
        expectedMatches: resultsData.length * (1/6), // 1/6 chance of matching your sign
        observedMatches: stats.correctMatches,
        degreesOfFreedom: 1
      });
    }
  }, [resultsData, loading, setChiSquareResults]);

  // Update filters when resultsData changes
  useEffect(() => {
    if (resultsData && resultsData.length > 0) {
      const beliefLevels = Array.from(new Set(resultsData
        .map(r => r.beliefLevel || 'Not provided')));
      const knowledgeLevels = Array.from(new Set(resultsData
        .map(r => r.knowledgeLevel || 'Not provided')));
      const horoscopeReadings = Array.from(new Set(resultsData
        .map(r => r.horoscopeReading || 'Not provided')));
      const astrologicalInfluences = Array.from(new Set(resultsData
        .map(r => r.astrologicalInfluence || 'Not provided')));

      setFilters({
        beliefLevel: [...new Set([...DEFAULT_FILTER_OPTIONS.beliefLevel, ...beliefLevels])],
        knowledgeLevel: [...new Set([...DEFAULT_FILTER_OPTIONS.knowledgeLevel, ...knowledgeLevels])],
        horoscopeReading: [...new Set([...DEFAULT_FILTER_OPTIONS.horoscopeReading, ...horoscopeReadings])],
        astrologicalInfluence: [...new Set([...DEFAULT_FILTER_OPTIONS.astrologicalInfluence, ...astrologicalInfluences])]
      });

      // Calculate initial filter counts
      const calculateInitialCount = (options: string[]): number => {
        return options.length;
      };

      setFilterCounts({
        beliefLevel: calculateInitialCount(beliefLevels),
        knowledgeLevel: calculateInitialCount(knowledgeLevels),
        horoscopeReading: calculateInitialCount(horoscopeReadings),
        astrologicalInfluence: calculateInitialCount(astrologicalInfluences)
      });
    }
  }, [resultsData]);

  // Update filter counts when filters change
  useEffect(() => {
    const calculateFilterCount = (filterKey: keyof FilterState): number => {
      // Get all possible options for this filter
      const allOptions = Array.from(new Set(resultsData.map(r => r[filterKey] || 'Not provided')));
      const selectedOptions = filters[filterKey];
      
      // If all options are selected or no options are selected, return 0
      if (selectedOptions.length === allOptions.length || selectedOptions.length === 0) {
        return 0;
      }
      
      // Count how many options are NOT selected (these are the active filters)
      const unselectedCount = allOptions.length - selectedOptions.length;
      return unselectedCount;
    };

    if (resultsData && resultsData.length > 0) {
      setFilterCounts({
        beliefLevel: calculateFilterCount('beliefLevel'),
        knowledgeLevel: calculateFilterCount('knowledgeLevel'),
        horoscopeReading: calculateFilterCount('horoscopeReading'),
        astrologicalInfluence: calculateFilterCount('astrologicalInfluence')
      });
    }
  }, [filters, resultsData]);

  const recalculateStatistics = () => {
    console.log('Recalculating statistics with filtered data length:', filteredData.length);
    const stats = calculateStatistics(filteredData);
    setResults(stats);
    
    // Update chiSquareResults state
    setChiSquareResults({
      chiSquare: stats.chiSquare,
      pValue: stats.pValue,
      isSignificant: stats.isSignificant,
      expectedMatches: filteredData.length * (1/6), // 1/6 chance of matching your sign
      observedMatches: stats.correctMatches,
      degreesOfFreedom: 1
    });
  };

  const handleClearData = async () => {
    // Only require password in production mode
    if (!isDevelopment()) {
      setShowPasswordModal(true);
    } else {
      try {
        await clearSurveyResults();
        clearAllResults();
      } catch (error) {
        console.error('Error clearing data:', error);
      }
    }
  };

  const handlePasswordSubmit = async () => {
    if (password === adminSettings.password) {
      try {
        await clearSurveyResults();
        clearAllResults();
        setShowPasswordModal(false);
        setPassword('');
        setPasswordError('');
      } catch (error) {
        console.error('Error clearing data:', error);
        setPasswordError('Failed to clear data');
      }
    } else {
      setPasswordError('Incorrect password');
    }
  };

  // Add sort state
  const [sortState, setSortState] = useState<SortState>({ column: 'timestamp', direction: 'desc' });

  // Add sort function
  const sortData = (data: SurveyResult[]) => {
    return [...data].sort((a, b) => {
      const aValue = a[sortState.column as keyof SurveyResult];
      const bValue = b[sortState.column as keyof SurveyResult];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortState.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortState.direction === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
      
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortState.direction === 'asc' 
          ? (aValue === bValue ? 0 : aValue ? -1 : 1)
          : (aValue === bValue ? 0 : aValue ? 1 : -1);
      }
      
      return 0;
    });
  };

  // Add sort handler
  const handleSort = (column: string) => {
    setSortState(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Update setFilters to not use prev parameter
  const handleFilterToggle = (option: string, filterKey: keyof FilterState) => {
    setFilters({
      ...filters,
      [filterKey]: filters[filterKey].includes(option)
        ? filters[filterKey].filter(o => o !== option)
        : [...filters[filterKey], option]
    });
  };

  // Calculate statistics
  const totalResponses = filteredData.length;
  const totalMatches = filteredData.filter((r: ResultData) => r.isMatch).length;
  const realSelections = filteredData.filter((r: ResultData) => r.selectedIsReal).length;
  
  // Calculate percentages
  const matchPercentage = totalResponses > 0 ? ((totalMatches / totalResponses) * 100).toFixed(1) : "0.0";
  const realPercentage = totalResponses > 0 ? ((realSelections / totalResponses) * 100).toFixed(1) : "0.0";
  
  // Calculate expected values
  const expectedMatches = totalResponses / 6; // 1/6 chance of matching your sign
  const expectedMatchesPercentage = totalResponses > 0 ? ((expectedMatches / totalResponses) * 100).toFixed(1) : "0.0";
  
  // Count selections by sign using filteredData
  const signSelections = {} as Record<string, number>;
  realDescriptions.forEach((desc: Description) => {
    signSelections[desc.id] = filteredData.filter((r: ResultData) => r.selectedDescription === desc.id).length;
  });
  
  // Count fake selections using filteredData
  const fakeSelectionCounts = {} as Record<string, number>;
  fakeDescriptions.forEach((desc: Description) => {
    fakeSelectionCounts[desc.id] = filteredData.filter((r: ResultData) => r.selectedDescription === desc.id).length;
  });
  
  // Prepare data for charts
  const signChartData = Object.entries(signSelections).map(([id, count]) => {
    const sign = realDescriptions.find((d: Description) => d.id === id);
    return {
      name: sign?.name,
      selections: count,
      percentage: totalResponses > 0 ? ((count / totalResponses) * 100).toFixed(1) : "0.0"
    };
  });
  
  const fakeChartData = Object.entries(fakeSelectionCounts).map(([id, count]) => {
    const sign = fakeDescriptions.find((d: Description) => d.id === id);
    return {
      name: sign?.name,
      selections: count,
      percentage: totalResponses > 0 ? ((count / totalResponses) * 100).toFixed(1) : "0.0"
    };
  });
  
  // Count actual signs using filteredData
  const actualSignCounts = {} as Record<string, number>;
  realDescriptions.forEach((desc: Description) => {
    actualSignCounts[desc.id] = filteredData.filter((r: ResultData) => r.actualSign === desc.id).length;
  });
  
  // Prepare data for actual signs chart
  const actualSignChartData = Object.entries(actualSignCounts).map(([id, count]) => {
    const sign = realDescriptions.find((d: Description) => d.id === id);
    return {
      name: sign?.name,
      count: count,
      percentage: totalResponses > 0 ? ((count / totalResponses) * 100).toFixed(1) : "0.0"
    };
  });

  // Add scroll to top when tab changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<SurveyResult | null>(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletePasswordError, setDeletePasswordError] = useState('');

  const handleDeleteClick = (record: SurveyResult) => {
    // Only require password in production mode
    if (!isDevelopment()) {
      setRecordToDelete(record);
      setShowDeleteModal(true);
    } else {
      if (onDeleteResult) {
        onDeleteResult(record.id);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (deletePassword === adminSettings.password) {
      if (recordToDelete && onDeleteResult) {
        try {
          await onDeleteResult(recordToDelete.id);
          setShowDeleteModal(false);
          setDeletePassword('');
          setDeletePasswordError('');
          setRecordToDelete(null);
        } catch (error) {
          console.error('Error deleting record:', error);
          setDeletePasswordError('Failed to delete record');
        }
      }
    } else {
      setDeletePasswordError('Incorrect password');
    }
  };

  if (loading) return <div className="section">Loading analysis...</div>;
  if (error) return <div className="section error">{error}</div>;
  if (!results) return <div className="section">No results available</div>;

  // COLORS for charts
  const COLORS = ['#6a4c93', '#8a76ab', '#c490d1', '#efbbff', '#ffc8dd', '#ffafcc', '#bde0fe', '#a2d2ff', '#cdb4db'];
  const FAKE_COLORS = ['#ff5252', '#ff7b7b', '#ff9e9e'];

  return (
    <div className="section analysis-section">
      <h2>Survey Data Analysis</h2>
      
      <div className="tabs">
        <button 
          className={`tab ${activeTab === "summary" ? "active" : ""}`}
          onClick={() => setActiveTab("summary")}
        >
          Summary
        </button>
        <button 
          className={`tab ${activeTab === "charts" ? "active" : ""}`}
          onClick={() => setActiveTab("charts")}
        >
          Charts
        </button>
        <button 
          className={`tab ${activeTab === "rawData" ? "active" : ""}`}
          onClick={() => setActiveTab("rawData")}
        >
          Raw Data
        </button>
      </div>
      
      <div className="tab-content">
        {activeTab === "summary" && (
          <div className="summary-tab">
            <div className="filters-container">
              <p className="filters-label">Filters</p>
              <div className="filter-buttons">
                <button 
                  className="filter-button"
                  onClick={() => setActiveFilter('beliefLevel')}
                >
                  Belief Level
                  {filterCounts.beliefLevel > 0 && (
                    <span className="filter-count">({filterCounts.beliefLevel})</span>
                  )}
                </button>
                <button 
                  className="filter-button"
                  onClick={() => setActiveFilter('knowledgeLevel')}
                >
                  Knowledge Level
                  {filterCounts.knowledgeLevel > 0 && (
                    <span className="filter-count">({filterCounts.knowledgeLevel})</span>
                  )}
                </button>
                <button 
                  className="filter-button"
                  onClick={() => setActiveFilter('horoscopeReading')}
                >
                  Horoscope Reading
                  {filterCounts.horoscopeReading > 0 && (
                    <span className="filter-count">({filterCounts.horoscopeReading})</span>
                  )}
                </button>
                <button 
                  className="filter-button"
                  onClick={() => setActiveFilter('astrologicalInfluence')}
                >
                  Astrological Influence
                  {filterCounts.astrologicalInfluence > 0 && (
                    <span className="filter-count">({filterCounts.astrologicalInfluence})</span>
                  )}
                </button>
              </div>
              <button 
                className="btn-primary recalculate-button"
                onClick={recalculateStatistics}
                disabled={loading}
              >
                {loading ? 'Recalculating...' : 'Recalculate Statistics'}
              </button>
            </div>

            <FilterModal
              isOpen={activeFilter === 'beliefLevel'}
              onClose={() => setActiveFilter(null)}
              title="Filter by Belief Level"
              options={Array.from(new Set(resultsData.map(r => r.beliefLevel || 'Not provided')))}
              selectedOptions={filters.beliefLevel}
              onToggleOption={(option) => handleFilterToggle(option, 'beliefLevel')}
            />

            <FilterModal
              isOpen={activeFilter === 'knowledgeLevel'}
              onClose={() => setActiveFilter(null)}
              title="Filter by Knowledge Level"
              options={Array.from(new Set(resultsData.map(r => r.knowledgeLevel || 'Not provided')))}
              selectedOptions={filters.knowledgeLevel}
              onToggleOption={(option) => handleFilterToggle(option, 'knowledgeLevel')}
            />

            <FilterModal
              isOpen={activeFilter === 'horoscopeReading'}
              onClose={() => setActiveFilter(null)}
              title="Filter by Horoscope Reading"
              options={Array.from(new Set(resultsData.map(r => r.horoscopeReading || 'Not provided')))}
              selectedOptions={filters.horoscopeReading}
              onToggleOption={(option) => handleFilterToggle(option, 'horoscopeReading')}
            />

            <FilterModal
              isOpen={activeFilter === 'astrologicalInfluence'}
              onClose={() => setActiveFilter(null)}
              title="Filter by Astrological Influence"
              options={Array.from(new Set(resultsData.map(r => r.astrologicalInfluence || 'Not provided')))}
              selectedOptions={filters.astrologicalInfluence}
              onToggleOption={(option) => handleFilterToggle(option, 'astrologicalInfluence')}
            />

            <div className="summary-card">
              <h3>Primary Question Results</h3>
              <p className="question">Do people choose their <em>actual</em> zodiac sign's description more often than expected by chance?</p>
              
              <div className="stat-grid">
                <div className="stat-item">
                  <h4>Total Responses</h4>
                  <span className="stat-value">{totalResponses}</span>
                </div>
                <div className="stat-item">
                  <h4>Correct Matches</h4>
                  <span className="stat-value">{totalMatches}</span>
                </div>
                <div className="stat-item">
                  <h4>Match Rate</h4>
                  <span className="stat-value">{matchPercentage}%</span>
                </div>
                <div className="stat-item">
                  <h4>Expected by Chance</h4>
                  <span className="stat-value">{expectedMatchesPercentage}%</span>
                </div>
              </div>
              
              <div className={`analysis-result ${chiSquareResults?.isSignificant ? 'significant' : 'not-significant'}`}>
                <h4 className="rainbow-text">
                  <span className="emoji">📊</span>
                  Statistical Analysis
                  <span className="emoji">🧪</span>
                </h4>
                {totalResponses > 0 ? (
                  <div className="analysis-text">
                    {filteredData.length < 60 && (
                      <p className="warning">
                        ⚠️ Warning: Small sample size ({filteredData.length} responses). A minimum of 60 responses is recommended for statistically reliable results.
                      </p>
                    )}
                    <p>Chi-Square Test Result: {chiSquareResults?.chiSquare.toFixed(4)}</p>
                    <p>P-Value: {chiSquareResults?.pValue.toFixed(4)}</p>
                    <p>
                      {chiSquareResults?.isSignificant 
                        ? 'The results are statistically significant (p ≤ 0.05), suggesting that participants are not selecting their signs randomly.'
                        : 'The results are not statistically significant (p > 0.05), suggesting that participants are selecting their signs randomly.'}
                    </p>
                  </div>
                ) : (
                  <p className="analysis-text">Not enough data to perform statistical analysis.</p>
                )}

                {/* Add Result Banner */}
                {totalResponses > 0 && (
                  <div className={`result-banner ${chiSquareResults?.isSignificant ? 'significant' : 'not-significant'}`}>
                    <div className="spinning-icon left">
                      {chiSquareResults?.isSignificant ? (
                        <span className="star-icon">⭐</span>
                      ) : (
                        <span className="beaker-icon">🧪</span>
                      )}
                    </div>
                    <h2 className={`result-text ${chiSquareResults?.isSignificant ? 'significant' : 'not-significant'}`}>
                      {chiSquareResults?.isSignificant ? 'ASTROLOGY WINS!' : 'ASTRONOMY WINS!'}
                    </h2>
                    <div className="spinning-icon right">
                      {chiSquareResults?.isSignificant ? (
                        <span className="star-icon">⭐</span>
                      ) : (
                        <span className="beaker-icon">🧪</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === "charts" && (
          <div className="charts-tab">
            <div className="chart-container">
              <h3>Match vs Unmatch Distribution</h3>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: 'Matched',
                        value: filteredData.filter(r => r.isMatch).length,
                        fill: '#2e7d32' // Green
                      },
                      {
                        name: 'Unmatched',
                        value: filteredData.filter(r => !r.isMatch).length,
                        fill: '#c62828' // Red
                      }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    outerRadius={120}
                    dataKey="value"
                  >
                    <Cell fill="#2e7d32" />
                    <Cell fill="#c62828" />
                  </Pie>
                  <Tooltip 
                    formatter={(value: number) => [`${value} participants`, 'Count']}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <p className="chart-note">
                Distribution of participants who matched vs unmatched their zodiac sign.
                In our blinded study, we'd expect ~16.6% match if entirely by chance (1/6).
              </p>
            </div>

            <div className="chart-container">
              <h3>Actual Zodiac Sign Distribution</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={actualSignChartData} margin={{ top: 20, right: 30, left: 60, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100}
                    interval={0}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    label={{ 
                      value: 'Number of Participants', 
                      angle: -90, 
                      position: 'insideLeft', 
                      offset: -40,
                      style: { textAnchor: 'middle' }
                    }} 
                  />
                  <Tooltip formatter={(value) => [`${value} participants`, 'Count']} />
                  <Legend />
                  <Bar dataKey="count" name="Number of Participants" fill="#8884d8">
                    {actualSignChartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="chart-note">Distribution of participants' actual zodiac signs</p>
            </div>
            
            <div className="chart-container">
              <h3>Real Sign Selection Distribution</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart 
                  data={signChartData.map(sign => {
                    // Get the actual count of participants with this sign
                    const actualSignCount = actualSignCounts[sign.name?.toLowerCase() || ''] || 0;
                    // Get the number of correct selections for this sign
                    const correctSelections = filteredData.filter(r => 
                      r.actualSign === sign.name?.toLowerCase() && 
                      r.selectedDescription === sign.name?.toLowerCase()
                    ).length;
                    // Calculate percentage, capped at 100%
                    const percentage = actualSignCount > 0 
                      ? Math.min((correctSelections / actualSignCount * 100), 100) 
                      : 0;
                    
                    return {
                      ...sign,
                      actualCount: actualSignCount,
                      correctSelections: correctSelections,
                      percentage: percentage
                    };
                  })} 
                  margin={{ top: 20, right: 30, left: 60, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100}
                    interval={0}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    label={{ 
                      value: 'Percentage of Actual Sign', 
                      angle: -90, 
                      position: 'insideLeft', 
                      offset: -40,
                      style: { textAnchor: 'middle' }
                    }}
                    domain={[0, 100]}
                  />
                  <Tooltip 
                    formatter={(value: number, name: string, props: any) => {
                      const data = props.payload;
                      if (!data) {
                        return ['No data available', 'Details'];
                      }
                      return [
                        `Total Participants: ${data.actualCount}\nCorrect Selections: ${data.correctSelections}\nAccuracy: ${value.toFixed(1)}%`,
                        'Details'
                      ];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="percentage" name="Percentage of Actual Sign" fill="#8884d8">
                    {signChartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="chart-note">Percentage of participants who selected their actual zodiac sign</p>
            </div>
            
            <div className="chart-container">
              <h3>Fake Sign Selection Distribution</h3>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={[
                      ...fakeChartData,
                      {
                        name: 'All Real Signs Combined',
                        selections: realSelections,
                        percentage: realPercentage
                      }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    outerRadius={120}
                    fill="#ff5252"
                    dataKey="selections"
                  >
                    {fakeChartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={FAKE_COLORS[index % FAKE_COLORS.length]} />
                    ))}
                    <Cell key="cell-real" fill="#8884d8" />
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} selections`, 'Count']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <p className="chart-note">Distribution of selections among fake zodiac descriptions compared to total real sign selections</p>
            </div>
          </div>
        )}
        
        {activeTab === "rawData" && (
          <div className="raw-data-tab">
            <h3>Raw Survey Data</h3>
            <p className="table-instructions">Click any column header to sort the data</p>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('participantName')} className="sortable">
                      Name {sortState.column === 'participantName' && (sortState.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('birthday')} className="sortable">
                      Birthday {sortState.column === 'birthday' && (sortState.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('selectedDescription')} className="sortable">
                      Selected Sign {sortState.column === 'selectedDescription' && (sortState.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('actualSign')} className="sortable">
                      Actual Sign {sortState.column === 'actualSign' && (sortState.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('isMatch')} className="sortable">
                      Match? {sortState.column === 'isMatch' && (sortState.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('beliefLevel')} className="sortable">
                      Belief Level {sortState.column === 'beliefLevel' && (sortState.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('knowledgeLevel')} className="sortable">
                      Knowledge Level {sortState.column === 'knowledgeLevel' && (sortState.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('horoscopeReading')} className="sortable">
                      Horoscope Reading {sortState.column === 'horoscopeReading' && (sortState.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('astrologicalInfluence')} className="sortable">
                      Astrological Influence {sortState.column === 'astrologicalInfluence' && (sortState.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortData(resultsData).map((result) => {
                    const selectedSign = realDescriptions.find(d => d.id === result.selectedDescription)?.name || 
                                      fakeDescriptions.find(d => d.id === result.selectedDescription)?.name || 
                                      result.selectedDescription;
                    const actualSign = realDescriptions.find(d => d.id === result.actualSign)?.name || result.actualSign;
                    
                    return (
                      <tr key={result.timestamp}>
                        <td>{result.participantName || 'Anonymous'}</td>
                        <td>{result.birthday || 'Not provided'}</td>
                        <td>{selectedSign}</td>
                        <td>{actualSign}</td>
                        <td className={result.isMatch ? 'match-yes' : 'match-no'}>
                          {result.isMatch ? 'Yes' : 'No'}
                        </td>
                        <td>{result.beliefLevel || 'Not provided'}</td>
                        <td>{result.knowledgeLevel || 'Not provided'}</td>
                        <td>{result.horoscopeReading || 'Not provided'}</td>
                        <td>{result.astrologicalInfluence || 'Not provided'}</td>
                        <td>
                          <button 
                            className="btn-danger btn-small"
                            onClick={() => handleDeleteClick(result)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            <div className="export-controls">
              <button className="btn-secondary" onClick={() => {
                // Convert data to CSV format
                const headers = [
                  'Name',
                  'Birthday',
                  'Selected Sign',
                  'Actual Sign',
                  'Match',
                  'Belief Level',
                  'Knowledge Level',
                  'Horoscope Reading',
                  'Astrological Influence'
                ];

                const csvRows = [
                  headers.join(','), // Header row
                  ...resultsData.map(result => {
                    const selectedSign = realDescriptions.find(d => d.id === result.selectedDescription)?.name || 
                                      fakeDescriptions.find(d => d.id === result.selectedDescription)?.name || 
                                      result.selectedDescription;
                    const actualSign = realDescriptions.find(d => d.id === result.actualSign)?.name || result.actualSign;
                    
                    return [
                      `"${(result.participantName || 'Anonymous').replace(/"/g, '""')}"`,
                      `"${(result.birthday || 'Not provided').replace(/"/g, '""')}"`,
                      `"${selectedSign.replace(/"/g, '""')}"`,
                      `"${actualSign.replace(/"/g, '""')}"`,
                      result.isMatch ? 'Yes' : 'No',
                      `"${(result.beliefLevel || 'Not provided').replace(/"/g, '""')}"`,
                      `"${(result.knowledgeLevel || 'Not provided').replace(/"/g, '""')}"`,
                      `"${(result.horoscopeReading || 'Not provided').replace(/"/g, '""')}"`,
                      `"${(result.astrologicalInfluence || 'Not provided').replace(/"/g, '""')}"`
                    ].join(',');
                  })
                ].join('\n');

                // Create and download the CSV file
                const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.download = 'zodiac_survey_results.csv';
                a.href = url;
                a.click();
              }}>
                Export Data (CSV)
              </button>
              <button className="btn-danger" onClick={handleClearData}>
                Clear All Data
              </button>
            </div>
          </div>
        )}
      </div>
      
      <div className="button-container">
        <button onClick={toggleAnalysisMode} className="btn-primary">
          Back to Survey
        </button>
      </div>

      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Enter Admin Password</h3>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="password-input"
            />
            {passwordError && <p className="error-message">{passwordError}</p>}
            <div className="modal-buttons">
              <button onClick={handlePasswordSubmit} className="btn-primary">
                Confirm
              </button>
              <button onClick={() => {
                setShowPasswordModal(false);
                setPassword('');
                setPasswordError('');
              }} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Delete Record</h3>
            <p>Are you sure you want to delete this record? This action cannot be undone.</p>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Enter admin password"
              className="password-input"
            />
            {deletePasswordError && <p className="error-message">{deletePasswordError}</p>}
            <div className="modal-buttons">
              <button onClick={handleDeleteConfirm} className="btn-danger">
                Delete
              </button>
              <button onClick={() => {
                setShowDeleteModal(false);
                setDeletePassword('');
                setDeletePasswordError('');
                setRecordToDelete(null);
              }} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisPage;